import React from 'react'
import { Drawer, Button, Card, Spin, Alert } from 'antd'
import { FileTextOutlined, CloseOutlined, RightCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { useAction } from '@agro-club/frontend-shared'
import { getRelativeTime } from 'modules/utils/helpers'
import { useUserNotificationList } from 'modules/domain/userNotifications/hooks'
import { formatNotificationTitle, formatNotificationText } from 'modules/domain/userNotifications/helpers'
import { Progress } from 'modules/types'
import DigitalAssetRoutes from 'views/pages/DigitalAsset/routes'
import UserNotificationActions from 'modules/domain/userNotifications/duck'

interface Notification {
  id: string
  title: string
  description: string
  timestamp: Date | string
  companyName?: string
}

interface NotificationsDrawerProps {
  open: boolean
  onClose: () => void
}

const NotificationCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-body {
    padding: 16px 40px 16px 16px;
  }
`

const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`

const NotificationIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #1890ff;
  border-radius: 8px;
  flex-shrink: 0;

  .anticon {
    color: white;
    font-size: 18px;
  }
`

const NotificationText = styled.div`
  flex: 1;
  min-width: 0;
`

const NotificationTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #000;
  margin-bottom: 4px;
  line-height: 1.4;
`

const NotificationDescription = styled.div`
  font-weight: 400;
  font-size: 14px;
  color: #000;
  margin-bottom: 8px;
  line-height: 1.4;
`

const NotificationTimestamp = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 12px;
`

const DismissButton = styled(Button)`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #8c8c8c;

  &:hover {
    color: #595959;
    background: transparent;
  }
`

const ViewButton = styled(Button)`
  margin-top: 8px;
  border-radius: 4px;
  background-color: #1890ff !important;
  border-color: #1890ff !important;

  &:hover {
    background-color: #40a9ff !important;
    border-color: #40a9ff !important;
  }
`

const NotificationsDrawer: React.FC<NotificationsDrawerProps> = ({ open, onClose }) => {
  const [progress, apiNotifications] = useUserNotificationList()
  const history = useHistory()
  const dismissAction = useAction(UserNotificationActions.dismissRequested)

  // Transform API notifications to match our interface
  const notifications: Notification[] = (apiNotifications || []).map((notification) => ({
    id: notification.id,
    title: formatNotificationTitle(notification),
    description: formatNotificationText(notification),
    timestamp: notification.created_at,
    companyName: undefined, // Company name not available in current API response
  }))

  const handleDismiss = (notificationId: string) => {
    dismissAction(notificationId)
  }

  const handleView = () => {
    history.push(DigitalAssetRoutes.List)
    onClose()
  }

  const loading = progress === Progress.WORK
  const error = progress === Progress.ERROR

  return (
    <Drawer
      title="Notifications"
      closable={{ 'aria-label': 'Close Button' }}
      closeIcon={<RightCircleOutlined />}
      onClose={onClose}
      open={open}
      placement="right"
      width={400}
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      )}

      {error && (
        <Alert
          message="Error loading notifications"
          description="Failed to load notifications. Please try again."
          type="error"
          style={{ margin: '16px' }}
        />
      )}

      {!loading && !error && notifications.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8c8c8c' }}>No notifications available</div>
      )}

      {!loading &&
        !error &&
        notifications.map((notification) => (
          <NotificationCard key={notification.id} style={{ position: 'relative' }}>
            <DismissButton
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => handleDismiss(notification.id)}
              aria-label="Dismiss notification"
            />
            <NotificationContent>
              <NotificationIcon>
                <FileTextOutlined />
              </NotificationIcon>
              <NotificationText>
                <NotificationTitle>{notification.title}</NotificationTitle>
                <NotificationDescription>{notification.description}</NotificationDescription>
                <NotificationTimestamp>{getRelativeTime(notification.timestamp)}</NotificationTimestamp>
                <ViewButton type="primary" onClick={handleView}>
                  View
                </ViewButton>
              </NotificationText>
            </NotificationContent>
          </NotificationCard>
        ))}
    </Drawer>
  )
}

export default NotificationsDrawer
